import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { NgTemplateOutlet } from '@angular/common';
import { CommonDirectives } from '@app/shared/directives/common-directives';
import { HrComponent } from '@app/shared/components/hr/hr.component';
import { us_states } from '@app/shared/interfaces/us_states';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { Subject, take, takeUntil } from 'rxjs';
import { environment } from '@env/environment';
import { MatStepperNavigationComponent } from '@app/shared/components/mat-stepper-navigation/mat-stepper-navigation.component';
import { ClinicSearchDropdownComponent } from '@app/shared/components/clinic-search-dropdown/clinic-search-dropdown.component';
import { UserRegistrationRequest } from '../../interfaces/userRegistrationRequest.interface';
import { LoadSpinnerWrapperComponent } from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import { RegistrationService } from '../../services/registration.service';
import { UserAvailability } from '../../interfaces/userAvailability.interface';
import { CustomValidators } from '@app/shared/services/customValidators';
import { Response } from '@app/shared/interfaces/response.interface';
import { ToastService } from '@app/core/services/toast.service';
import { AccountTypes } from '@app/shared/enums/accountTypes.enum';
import { HeroBannerComponent } from '@app/shared/components/hero-banner/hero-banner.component';
import { ProviderMultiselectComponent } from '@app/shared/components/provider-multiselect/provider-multiselect.component';

interface PersonalInfoType {
  value: number;
  label: string;
}

@Component({
  selector: 'app-portal-registration',
  templateUrl: './portal-registration.component.html',
  styleUrl: './portal-registration.component.scss',
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: {displayDefaultIndicatorType: false},
    },
    provideNgxMask()
  ],
  imports: [
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    NgTemplateOutlet,
    NgxMaskDirective,
    MatDialogModule,
    MatCheckboxModule,
    CommonDirectives,
    HrComponent,
    MatStepperNavigationComponent,
    ClinicSearchDropdownComponent,
    ProviderMultiselectComponent,
    LoadSpinnerWrapperComponent,
    HeroBannerComponent
  ]
})
export class PortalRegistrationComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') private stepper!: MatStepper;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private registrationService: RegistrationService,
    private customValidators: CustomValidators,
    private toastService: ToastService
  ) {}

  loading = false;
  selectedType: number | null = null;
  readonly UserAccountTypes = AccountTypes;
  readonly us_states_list = us_states;
  readonly privacyPolicyUrl: string = environment.privacyPolicyUrl;
  readonly termsOfUseUrl: string = environment.termsOfUseUrl;
  stepSubmitted: boolean[] = [false];
  stepCompleted: boolean[] = [false];
  currentStep = 0;
  ngDestroy$ = new Subject();
  stepMessage = 'Fill out the form to request access to our partner portal. Approval can take up to two business days.';
  thankYouMessage = 'Approval can take up to two business days.';
  stepperMessage: string = this.stepMessage;
  personalInfoTypes: PersonalInfoType[] = [
    {value: 1, label: 'Case Manager'},
    {value: 2, label: 'Employer of Work Comp Patient'},
    {value: 3, label: 'Insurance Adjuster'},
    {value: 4, label: 'Referring Clinic'}
  ];

  piForm: FormGroup = this.fb.group({
    accountType: [null],
    firstName: [null, Validators.required],
    lastName: [null, Validators.required],
    email: [null, [Validators.required, Validators.email]],
    phone: [null, Validators.required],
    jobTitle: [null],
    fax: [null],
    street: [null],
    city: [null],
    state: [null],
    zipcode: [null, [
      Validators.required,
      Validators.pattern(/^\d{5}$/)]]
  });

  siForm: FormGroup = this.fb.group({
    password: [null, [Validators.required, this.customValidators.passwordPolicyValidator]],
    confirmPassword: [null, Validators.required]
  },{ validators: this.customValidators.passwordMatchValidator('password', 'confirmPassword') });

  ciForm: FormGroup = this.fb.group({
    managerFirstName: [null, Validators.required],
    managerLastName: [null, Validators.required],
    managerEmail: [null, [Validators.required, Validators.email]],
    companyName: [null],
    faxForInformation: [null, Validators.required],
    faxForChartNote: [null, Validators.required],
    acceptedPrivacyPolicyAndTermsOfUse: [null]
  });

  cpInfoForm: FormGroup = this.fb.group({
    firstClinicId: [null, Validators.required],
    firstClinicProviderIds: [null, Validators.required],
    secondClinicId: [null],
    secondClinicProviderIds: [null],
    referralUserAcceptedPrivacyPolicyAndTermsOfUse: [null, Validators.requiredTrue],
    firstClinicOtherProviderName: [null],
    firstClinicOtherProviderNPI: [null],
    secondClinicOtherProviderName: [null],
    secondClinicOtherProviderNPI: [null]
  });
  firstClinicCtrl = this.cpInfoForm.controls['firstClinicId'] as FormControl;
  firstProvidersCtrl = this.cpInfoForm.controls['firstClinicProviderIds'] as FormControl;
  secondClinicCtrl = this.cpInfoForm.controls['secondClinicId'] as FormControl;
  secondProvidersCtrl = this.cpInfoForm.controls['secondClinicProviderIds'] as FormControl;
  firstClinicOtherProviderNameCtrl = this.cpInfoForm.controls['firstClinicOtherProviderName'] as FormControl;
  firstClinicOtherProviderNPICtrl = this.cpInfoForm.controls['firstClinicOtherProviderNPI'] as FormControl;
  secondClinicOtherProviderNameCtrl = this.cpInfoForm.controls['secondClinicOtherProviderName'] as FormControl;
  secondClinicOtherProviderNPICtrl = this.cpInfoForm.controls['secondClinicOtherProviderNPI'] as FormControl;

  ngOnInit(): void {
    this.setValidators();
    this.subscribeToEmailValueChanges();
    this.subscribeToSecondClinicValueChanges();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
  }

  clearValidations(formControls: Array<AbstractControl | null>) {
    formControls?.forEach(control => {
      control?.clearValidators();
      control?.setValue(null);
    });
  }

  addRequiredValidator(formControls: Array<AbstractControl | null>) {
    formControls?.forEach(control => {
      control?.setValidators([Validators.required]);
    });
  }

  onCancel() {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Confirm';
    dialog.componentInstance.content = 'Are you sure you want to cancel registration? Any entered information will be lost.';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => {
        this.returnToIowaOrthoWebsite();
      });
  }

  handleNext() {
    switch (this.stepper.selectedIndex) {
      case 0:
        this.stepSubmitted[0] = true;
        this.piForm.markAllAsTouched();   // Marking all form control as touched to allow error messages to display
        this.siForm.markAllAsTouched();   // Marking all form control as touched to allow error messages to display
        if (this.piForm.valid && this.siForm.valid) {
          // Check if email is available and also make sure that password meets the requirements
          this.checkEmailAvailability(this.piForm.get('email')?.value);
        }
        break;
      case 1:
        this.stepSubmitted[1] = true;
        this.ciForm.markAllAsTouched();   // Marking all form control as touched to allow error messages to display
        if (this.ciForm.valid) {
          this.stepCompleted[1] = true;
          this.goToNextStep();
        }
        break;
    }
  }

  goBack() {
    this.stepper.previous();
    this.currentStep--;
  }

  goToNextStep() {
    setTimeout(() => {
      this.stepper.next();
      this.currentStep++;
    }, 200);
  }

  startProcessToRegisterUser() {
    if (this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER) {    // If account type is Referral Partner
      this.stepSubmitted[2] = true;
      this.cpInfoForm.markAllAsTouched();   // Marking all form control as touched to allow error messages to display
      if (this.cpInfoForm.valid) {
        this.registerUser(this.createUserRegistrationRequest());
      }
    } else {  // If account type is not Referral Partner
      this.stepSubmitted[1] = true;
      this.ciForm.markAllAsTouched();   // Marking all form control as touched to allow error messages to display
      if (this.ciForm.valid) {
        this.registerUser(this.createUserRegistrationRequest());
      }
    }
  }

  returnToIowaOrthoWebsite() {
    window.location.href = environment.ioOrthoUrl;
  }

  setValidators() {
    this.piForm.get('accountType')?.valueChanges
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(actType => {
        this.stepCompleted = [false];
        this.stepSubmitted = [false];
        const jobTitleControl = this.piForm.get('jobTitle');
        const streetControl = this.piForm.get('street');
        const cityControl = this.piForm.get('city');
        const stateControl = this.piForm.get('state');
        const zipcodeControl = this.piForm.get('zipcode');
        const companyName = this.ciForm.get('companyName');
        const acceptedPrivacyPolicyAndTermsOfUse = this.ciForm.get('acceptedPrivacyPolicyAndTermsOfUse');
        const secondClinicProviderIdsControl = this.cpInfoForm.get('secondClinicProviderIds');

        if (actType === this.UserAccountTypes.REFERRAL_PARTNER) {    // If account type is Referral Partner
          this.addRequiredValidator([jobTitleControl]);
          this.clearValidations(
            [streetControl, cityControl, stateControl, zipcodeControl, companyName, acceptedPrivacyPolicyAndTermsOfUse]
          );
        } else {    // If account type is not Referral Partner
          zipcodeControl?.setValidators([Validators.required, Validators.pattern(/^\d{5}$/)]);
          acceptedPrivacyPolicyAndTermsOfUse?.setValidators(Validators.requiredTrue);
          this.addRequiredValidator([streetControl, cityControl, stateControl, companyName]);
          this.clearValidations([jobTitleControl, secondClinicProviderIdsControl]);
        }
      });
  }

  subscribeToEmailValueChanges() {
    this.piForm.get('email')?.valueChanges
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(() => {
        this.removeEmailTakenError();
      });
  }

  subscribeToSecondClinicValueChanges() {
    this.secondClinicCtrl?.valueChanges
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((val) => {
        if (val) {
          this.secondProvidersCtrl?.setValidators([Validators.required]);
        } else {
          this.secondProvidersCtrl?.clearValidators();
          this.secondProvidersCtrl?.setValue(null);
        }
        this.secondProvidersCtrl.updateValueAndValidity();
      });
  }

  removeEmailTakenError() {
    const control = this.piForm.get('email');
    if (control?.hasError('emailTaken')) {
      const errors = { ...control.errors };
      delete errors['emailTaken'];

      // If there are no other errors, set null, else set the remaining ones
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  registerUser(userRegistrationRequest: UserRegistrationRequest) {
    this.loading = true;
    this.registrationService.registerUser(userRegistrationRequest)
      .subscribe((response: Response<string>) => {
        this.loading = false;
        if (response.succeeded) {
          this.selectedType !== this.UserAccountTypes.REFERRAL_PARTNER ? this.stepCompleted[1] = true : this.stepCompleted[2] = true;
          this.stepCompleted[3] = true;
          this.stepperMessage = this.thankYouMessage;
          this.goToNextStep();
        } else {
          this.toastService.showErrorToast(response.errorMessages.join(', '));
        }
      });
  }

  private createUserRegistrationRequest(): UserRegistrationRequest {
    return {
      ...this.piForm.value,
      ...this.siForm.value,
      ...this.ciForm.value,
      ...this.cpInfoForm.value,
      firstClinicId:
        this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER ? this.cpInfoForm.get('firstClinicId')?.value.toString() : null,
      firstClinicProviderIds:
        this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER ? this.cpInfoForm.get('firstClinicProviderIds')?.value
          .filter((id: number) => id !== -1).map((id: number) => id.toString()) : null,
      secondClinicId:
        this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER && this.cpInfoForm.get('secondClinicId')?.value ? this.cpInfoForm.get('secondClinicId')?.value.toString() : null,
      secondClinicProviderIds:
        this.selectedType ===
        this.UserAccountTypes.REFERRAL_PARTNER && this.cpInfoForm.get('secondClinicProviderIds')?.value ? this.cpInfoForm.get('secondClinicProviderIds')?.value.filter((id: number) => id !== -1).map((id: number) => id.toString()) : null,
      firstClinicOtherProviderName:
        this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER ? this.firstClinicOtherProviderNameCtrl.value : null,
      firstClinicOtherProviderNPI:
        this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER ? this.firstClinicOtherProviderNPICtrl.value : null,
      secondClinicOtherProviderName:
        this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER ? this.secondClinicOtherProviderNameCtrl.value : null,
      secondClinicOtherProviderNPI:
        this.selectedType === this.UserAccountTypes.REFERRAL_PARTNER ? this.secondClinicOtherProviderNPICtrl.value : null,
      confirmPassword: null,
      accountTypeLookupId: this.selectedType
    } as UserRegistrationRequest;
  }

  checkEmailAvailability(email: string) {
    this.loading = true;
    this.registrationService.checkEmailAvailability(email)
      .subscribe((response: UserAvailability) => {
        let isEmailAvailable = false;
        this.loading = false;
        if (response.emailTaken)  {
          this.piForm.get('email')?.setErrors({ emailTaken: true });
        } else {
          this.removeEmailTakenError();
          isEmailAvailable = true;
        }
        if (isEmailAvailable) {
          this.stepCompleted[0] = true;
          this.goToNextStep();
        }
      });
  }
}
